/**
 * Was beim Laden des Bestands EINZELN ausfaellt und die Anzeige trotzdem tragfaehig laesst (Fehleranzeige Paket 2,
 * Task 5, Spec 2026-10-23-fehleranzeige-ladefehler-design.md, Regeln L2 und L5): die Zahlungsziel-Vorlagen, einzelne
 * Lieferanten, einzelne Freigabelisten. Bisher fiel das still aus der Anzeige (leere Auswahl, fehlende Lieferantendaten,
 * Dropdown ohne Option); jetzt entsteht je ART und Lauf EIN Stapeleintrag, mit der Anzahl und der Ursache des ersten
 * Fehlschlags.
 *
 * Der Lader (`bestandLaden`, `bestandTeilLaden` in `Speicher.tsx`) SAMMELT und liefert das Ergebnis als Feld `teilFehler`
 * neben dem Bestand; der Speicher MELDET es (`meldeBestandTeilFehler`) erst, NACHDEM er den Lauf uebernommen hat. Ein
 * gescheiterter oder ueberholter Lauf meldet nichts - auch dann nicht, wenn er einen Teilfehler mitbringt (L5, ein Fehler
 * je Ausfall: scheitert das Erstladen ganz, steht nur die Flaeche). Im Store steht der Teilfehler nie.
 */
import { meldeFehler } from "./fehlerStapel.ts";

/**
 * Die Teilfehler EINES Ladelaufs. Eine Art fehlt, wenn sie nicht ausgefallen ist.
 *
 * `vorlagen` ist die Ursache selbst; es zaehlt der SCHLUESSEL, nicht der Wert (eine mit `undefined` abgelehnte
 * Anfrage waere sonst "kein Fehler"). Lieferanten und Freigaben tragen die Zahl der gescheiterten Abrufe und die
 * Ursache des ERSTEN, in der Reihenfolge, in der sie angefragt wurden.
 */
export interface BestandTeilFehler {
  vorlagen?: unknown;
  lieferanten?: { anzahl: number; ursache: unknown };
  freigaben?: { anzahl: number; ursache: unknown };
}

/** L4: das Live-Nachladen nach einer Aenderung von anderer Seite scheiterte; der Datensatz in der Anzeige ist alt. */
export const LIVE_NACHLADEN_SATZ =
  "Eine Änderung von anderer Seite konnte nicht geladen werden – die Anzeige ist nicht aktuell. Bitte neu laden.";

/**
 * Das Nachladen nach "Konflikt uebernehmen" (Knopf im Banner) scheiterte. Der Nutzer hat den Knopf gedrueckt - darum
 * der Satz MIT Ursache (`meldeFehler`) und nicht der Live-Satz; der Konflikt bleibt stehen.
 */
export const KONFLIKT_STAND_SATZ = "Der aktuelle Stand konnte nicht geladen werden.";

const VORLAGEN_SATZ =
  "Die Zahlungsziel-Vorlagen konnten nicht geladen werden – die Auswahl der Zahlungsziele ist leer.";

function lieferantenSatz(anzahl: number): string {
  return anzahl === 1
    ? "Ein Lieferant konnte nicht geladen werden – bei seinen Bestellungen fehlen die Lieferantendaten."
    : `${anzahl} Lieferanten konnten nicht geladen werden – bei ihren Bestellungen fehlen die Lieferantendaten.`;
}

function freigabenSatz(anzahl: number): string {
  return anzahl === 1
    ? "Die Zahlungsziel-Freigaben eines Lieferanten konnten nicht geladen werden – dort bietet die Zahlungsziel-Auswahl keine Option an."
    : `Die Zahlungsziel-Freigaben von ${anzahl} Lieferanten konnten nicht geladen werden – dort bietet die Zahlungsziel-Auswahl keine Option an.`;
}

/**
 * Meldet die Teilfehler eines UEBERNOMMENEN Laufs, je Art ein Eintrag (`meldeFehler`: Abbruch und 401 bleiben als
 * Ursache still, gleiche Texte laufen im Stapel ueber den Zaehler zusammen).
 */
export function meldeBestandTeilFehler(teilFehler: BestandTeilFehler): void {
  if ("vorlagen" in teilFehler) meldeFehler(teilFehler.vorlagen, VORLAGEN_SATZ);
  const { lieferanten, freigaben } = teilFehler;
  if (lieferanten && lieferanten.anzahl >= 1) meldeFehler(lieferanten.ursache, lieferantenSatz(lieferanten.anzahl));
  if (freigaben && freigaben.anzahl >= 1) meldeFehler(freigaben.ursache, freigabenSatz(freigaben.anzahl));
}

/** Der Ausgang EINES Abrufs, dessen Scheitern den Lauf nicht abbrechen darf. */
export type Versuch<T> =
  | { readonly ok: true; readonly wert: T }
  | { readonly ok: false; readonly ursache: unknown };

/**
 * Macht aus einem Abruf, der ablehnen darf, einen `Versuch`: er lehnt NIE ab, und die Ursache geht nicht verloren
 * (anders als `.catch(() => Rueckfallwert)`, das den Fehler verschluckt).
 */
export function versuche<T>(abruf: Promise<T>): Promise<Versuch<T>> {
  return abruf.then(
    (wert): Versuch<T> => ({ ok: true, wert }),
    (ursache: unknown): Versuch<T> => ({ ok: false, ursache }),
  );
}

/**
 * Zaehlt die gescheiterten Versuche einer Art; die Ursache ist die des ersten in der Reihenfolge der Liste.
 * `undefined`, wenn keiner scheiterte.
 */
export function fehlschlaege(
  versuche: readonly Versuch<unknown>[],
): { anzahl: number; ursache: unknown } | undefined {
  let anzahl = 0;
  let ursache: unknown;
  for (const versuch of versuche) {
    if (versuch.ok) continue;
    if (anzahl === 0) ursache = versuch.ursache;
    anzahl += 1;
  }
  return anzahl === 0 ? undefined : { anzahl, ursache };
}

/** Haengt `teilFehler` an ein Ladeergebnis - und nur, wenn etwas ausfiel (ein leerer Teilfehler wird weggelassen). */
export function mitTeilFehler<T extends object>(
  ergebnis: T,
  teilFehler: BestandTeilFehler,
): T & { teilFehler?: BestandTeilFehler } {
  return Object.keys(teilFehler).length === 0 ? ergebnis : { ...ergebnis, teilFehler };
}
